/**
 * InvoicesView Component - Main invoices list view
 * Updated with mobile-responsive header, filter drawer, and action handlers
 */

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Drawer,
  IconButton,
} from "@mui/material";
import { Add, FilterList, FactCheck, Link as LinkIcon } from "@mui/icons-material";
import { useDeviceType } from "../../leads/hooks/useDeviceType.js";
import InvoiceList from "../components/InvoiceList.jsx";
import { getInvoices, markInvoiceAsSent } from "../services/invoiceService.js";
import { invoiceDownloadService } from "../services/downloadService.js";
import { logError, logInfo } from "@/shared/utils/logger.js";
import SendInvoiceModal from "../components/SendInvoiceModal.jsx";
import InvoicePdfAuditDrawer from "../components/InvoicePdfAuditDrawer.jsx";
import LinkOrphanInvoicesModal from "../components/LinkOrphanInvoicesModal.jsx";
import {
  PageShell,
} from "@/shared/components/pageShell";

/**
 * InvoicesView Component
 */
const InvoicesView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isMobile } = useDeviceType();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [sendInvoiceModalOpen, setSendInvoiceModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pdfAuditOpen, setPdfAuditOpen] = useState(false);
  const [linkOrphansModalOpen, setLinkOrphansModalOpen] = useState(false);
  const [downloadingPdfIds, setDownloadingPdfIds] = useState(new Set());

  const jobIdParam = searchParams.get("jobId");

  const pageTitle = useMemo(() => {
    if (jobIdParam) return `Invoices • Job ${jobIdParam}`;
    return "Invoices";
  }, [jobIdParam]);

  useEffect(() => {
    loadInvoices();
  }, [filter, jobIdParam]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const filters = {
        ...(filter !== "all" ? { status: filter } : {}),
        ...(jobIdParam ? { jobId: jobIdParam } : {}),
      };
      const result = await getInvoices(filters);
      if (result.success) {
        setInvoices(result.data || []);
        logInfo("Invoices loaded successfully", {
          count: result.data?.length || 0,
          filter,
          jobId: jobIdParam || null,
        });
      } else {
        setError(result.error || "Failed to load invoices");
        logError("Failed to load invoices", {
          error: result.error,
          filter,
          jobId: jobIdParam || null,
        });
      }
    } catch (err) {
      setError(err.message || "Failed to load invoices");
      logError("Failed to load invoices", {
        error: err.message,
        filter,
        jobId: jobIdParam || null,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = (invoice) => {
    navigate(`/invoices/${invoice.id || invoice._id}`, {
      state: { invoice },
    });
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      setDownloadingPdfIds((prev) => new Set(prev).add(invoiceId));
      await invoiceDownloadService.downloadPdf(invoiceId);
      logInfo("Invoice PDF downloaded", { invoiceId });
    } catch (err) {
      setError("Failed to download PDF");
      logError("Failed to download PDF", {
        invoiceId,
        error: err.message,
      });
    } finally {
      setDownloadingPdfIds((prev) => {
        const next = new Set(prev);
        next.delete(invoiceId);
        return next;
      });
    }
  };

  const handleSendInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setSendInvoiceModalOpen(true);
  };

  const handleRemindInvoice = async (invoice) => {
    // Navigate to invoice details where reminder can be sent
    // Or implement reminder service call here
    const invoiceId = invoice.id || invoice._id;
    navigate(`/invoices/${invoiceId}`, {
      state: { invoice, action: "remind" },
    });
    logInfo("Navigating to invoice for reminder", { invoiceId });
  };

  const handleFollowUpNavigate = (invoice) => {
    const invoiceIdValue = invoice?.id || invoice?._id;
    if (!invoiceIdValue) return;
    const followUpId =
      invoice?.followUpSummary?.nextFollowUpId ||
      invoice?.followUpSummary?.latestFollowUpId;
    if (followUpId) {
      navigate(`/followups/${followUpId}`);
    } else {
      navigate(`/followups?entityType=invoice&entityId=${invoiceIdValue}`);
    }
  };

  return (
    <PageShell>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h4">{pageTitle}</Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {isMobile ? (
            <>
              <IconButton
                onClick={() => setFilterDrawerOpen(true)}
                color="primary"
              >
                <FilterList />
              </IconButton>
              <IconButton
                onClick={() => setPdfAuditOpen(true)}
                color="primary"
                aria-label="Open PDF audit panel"
              >
                <FactCheck />
              </IconButton>
              <IconButton
                onClick={() => setLinkOrphansModalOpen(true)}
                color="primary"
                aria-label="Link orphan invoices"
              >
                <LinkIcon />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  const params = new URLSearchParams();
                  if (jobIdParam) params.set("jobId", jobIdParam);
                  navigate(`/invoices/new${params.toString() ? `?${params}` : ""}`);
                }}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                New Invoice
              </Button>
            </>
          ) : (
            <>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filter}
                  label="Filter by Status"
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <MenuItem value="all">All Invoices</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<FactCheck />}
                onClick={() => setPdfAuditOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                PDF Audit
              </Button>
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={() => setLinkOrphansModalOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Link Orphans
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  const params = new URLSearchParams();
                  if (jobIdParam) params.set("jobId", jobIdParam);
                  navigate(`/invoices/new${params.toString() ? `?${params}` : ""}`);
                }}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                New Invoice
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filter by Status
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filter}
              label="Status"
              onChange={(e) => {
                setFilter(e.target.value);
                setFilterDrawerOpen(false);
              }}
            >
              <MenuItem value="all">All Invoices</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Drawer>

      <InvoicePdfAuditDrawer
        open={pdfAuditOpen}
        onClose={() => setPdfAuditOpen(false)}
        jobId={jobIdParam}
      />

      <LinkOrphanInvoicesModal
        open={linkOrphansModalOpen}
        onClose={() => setLinkOrphansModalOpen(false)}
        onSuccess={() => {
          loadInvoices(); // Refresh invoice list after successful linking
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Invoice list */}
      <InvoiceList
        invoices={invoices}
        loading={loading}
        downloadingPdfIds={downloadingPdfIds}
        onViewInvoice={handleViewInvoice}
        onDownloadPdf={handleDownloadPdf}
        onSendInvoice={handleSendInvoice}
        onRemindInvoice={handleRemindInvoice}
        onFollowUpNavigate={handleFollowUpNavigate}
      />
      <SendInvoiceModal
        open={sendInvoiceModalOpen}
        onClose={() => {
          setSendInvoiceModalOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onSuccess={() => {
          loadInvoices(); // Refresh list
        }}
      />
    </PageShell>
  );
};

export default InvoicesView;
